import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useBusinessConfig } from '@/hooks/useBusinessConfig';
import { logEvent } from '@/lib/vbs/worksheets/med-log-logic';
import { updateStockAndCheckThreshold } from '@/lib/vbs/utils/inventory-logic';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Pill, UserCheck, AlertCircle, XCircle, Loader2 } from 'lucide-react';

interface InventoryItem {
  id: string;
  item_name: string;
  current_stock: number;
  reorder_point: number;
  unit_type: string;
}

export const MedicationLogUI = () => {
  const { config } = useBusinessConfig();
  const [loading, setLoading] = useState(false);
  const [meds, setMeds] = useState<InventoryItem[]>([]);
  const [fetching, setFetching] = useState(true);

  // 1. Load live stock from DB
  const loadInventory = async () => {
    setFetching(true);
    const { data, error } = await supabase
      .from('inventory')
      .select('*')
      .order('item_name', { ascending: true });

    if (!error && data) {
      setMeds(data);
    }
    setFetching(false);
  };

  useEffect(() => {
    loadInventory();
  }, []);

  // 2. The Heavy Lifter: Administer/Refuse Logic
  const handleAction = async (item: InventoryItem, action: 'ADMINISTERED' | 'REFUSED') => {
    if (!config) return;
    setLoading(true);

    try {
      // Create the Audit Log Entry (The Gold Standard)
      const auditSuccess = await logEvent('med-log-session', `MED_${action}`, {
        item_name: item.item_name,
        action: action,
        prev_balance: item.current_stock,
        new_balance: action === 'ADMINISTERED' ? item.current_stock - 1 : item.current_stock,
        timestamp: new Date().toISOString()
      });

      if (!auditSuccess) throw new Error("Audit Log Failed");

      // If Administered, subtract from stock and check for Low Stock Ping
      if (action === 'ADMINISTERED') {
        const pingUrl = await updateStockAndCheckThreshold(
          item.id,
          1,
          config.contact_phone
        );

        if (pingUrl) {
          toast.warning(`LOW STOCK: Notification required for ${item.item_name}`);
          // Open SMS link in new tab (works on mobile, safe on desktop)
          setTimeout(() => { 
            window.open(pingUrl, '_blank'); 
          }, 2000);
        }
      }

      toast.success(`${item.item_name} ${action.toLowerCase()} and logged.`);
      await loadInventory(); // Refresh UI with new numbers
    } catch (err) {
      console.error(err);
      toast.error("Process failed. Check database connection.");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return (
    <div className="flex justify-center p-20">
      <Loader2 className="animate-spin text-slate-300 w-10 h-10" />
    </div>
  );

  return (
    <div className="pt-4 space-y-4 pb-20">
      <header className="px-2">
        <h2 className="text-2xl font-black italic uppercase tracking-tighter text-slate-900 flex items-center gap-2">
          <Pill className="text-purple-600" /> Medication Chart
        </h2>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest italic">
          Internal Gold Standard • Audit Compliant
        </p>
      </header>

      {meds.length === 0 ? (
        <div className="bg-slate-100 rounded-[2rem] p-10 text-center">
          <p className="font-black uppercase text-slate-400 text-xs">No Medications in Vault</p>
          <p className="text-[10px] font-bold text-slate-400 mt-2">Add stock in Admin to begin</p>
        </div>
      ) : (
        <div className="space-y-3">
          {meds.map((item) => (
            <div key={item.id} className="bg-white border-2 border-slate-100 rounded-[2rem] p-5 shadow-sm">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-black text-slate-900 uppercase leading-none">
                    {item.item_name}
                  </h3>
                  <p className="text-[10px] font-bold text-purple-600 uppercase mt-1 tracking-widest">
                    Stock Inventory Item
                  </p>
                </div>
                <div className={`text-right px-3 py-1 rounded-xl border ${
                  item.current_stock <= item.reorder_point 
                  ? 'bg-red-50 border-red-100' 
                  : 'bg-slate-50 border-slate-100'
                }`}>
                  <p className="text-[8px] font-black text-slate-400 uppercase">Remaining</p>
                  <p className={`text-lg font-black leading-none ${
                    item.current_stock <= item.reorder_point ? 'text-red-600' : 'text-slate-900'
                  }`}>
                    {item.current_stock}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Button 
                  variant="outline" 
                  disabled={loading}
                  onClick={() => handleAction(item, 'REFUSED')}
                  className="h-12 rounded-xl border-slate-100 text-slate-500 font-black uppercase text-[10px] gap-2 hover:bg-red-50 hover:text-red-500"
                >
                  <XCircle className="w-4 h-4" /> Refusal
                </Button>
                <Button 
                  disabled={loading || item.current_stock <= 0}
                  onClick={() => handleAction(item, 'ADMINISTERED')}
                  className="h-12 rounded-xl bg-teal-500 hover:bg-teal-600 text-white font-black uppercase text-[10px] gap-2 shadow-lg shadow-teal-500/20"
                >
                  <UserCheck className="w-4 h-4" /> {item.current_stock <= 0 ? 'Out of Stock' : 'Administer'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100 flex gap-3">
        <AlertCircle className="text-amber-600 w-5 h-5 shrink-0" />
        <p className="text-[10px] font-bold text-amber-800 leading-tight uppercase opacity-80">
          Warning: Theft of medication is a criminal offense. Every interaction is logged with a unique ID and immutable timestamp.
        </p>
      </div>
    </div>
  );
};